import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useParams } from "react-router-dom";
import ReCaptcha from "react-google-recaptcha";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { Steps } from "primereact/steps";
import { Tag } from "primereact/tag";
import { PublicIntakeConfig } from "../../../models/intake";
import "./PublicIntakePage.css";

const LA_TIMEZONE = "America/Los_Angeles";

const losAngelesToday = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: LA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return new Date(Number(values.year), Number(values.month) - 1, Number(values.day), 12, 0, 0);
};

const formatDateOnly = (value: Date | null) => value
  ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
  : "";

const parseDateOnly = (value: string) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0) : null;
};

type ClinicalScreeningForm = Record<string, any>;

const REQUIRED_CLINICAL_FIELDS = [
  "needsDetox", "fCodes", "detoxCompletedDate", "drugOfChoice", "methodOfUse", "lastUseDate", "benzoUseLast30Days",
  "currentSuicidalIdeation", "suicidalIdeationLast90Days", "suicideAttemptHistory", "selfHarm", "psychoticSymptoms", "groupOrRoommateSuitability", "mentalHealthServicesBenefit", "cognitiveImpairments", "mentalHealthDiagnosis",
  "canUseStairs", "medicalCareOrLabsNext30Days", "hasMedicalDiagnoses", "dietaryRestrictions", "openWounds", "pregnant", "pregnancyDetails", "medicalHospitalizationLastSixMonths", "medicalHospitalizationDetails", "medicalProblems", "lifeThreateningIllness", "seizureHistory",
  "methadoneOrSuboxone", "medicationAssistedTreatment", "benzoStimulantOrOpioid", "hasMedicationsNext30Days",
  "developmentalDelays", "sexCrimeOrArson", "aggressiveTowardOthers", "knowsAnyoneAtFacility", "previousProgram", "willingToParticipate",
] as const;
const SAMPLE_YES_NO_FIELDS = [
  "needsDetox", "benzoUseLast30Days", "currentSuicidalIdeation", "suicidalIdeationLast90Days", "suicideAttemptHistory", "selfHarm", "psychoticSymptoms", "groupOrRoommateSuitability", "mentalHealthServicesBenefit", "cognitiveImpairments",
  "canUseStairs", "medicalCareOrLabsNext30Days", "hasMedicalDiagnoses", "onDialysis", "openWounds", "pregnant", "medicalHospitalizationLastSixMonths", "medicalProblems", "lifeThreateningIllness", "seizureHistory", "methadoneOrSuboxone", "benzoStimulantOrOpioid", "hasMedicationsNext30Days",
  "developmentalDelays", "sexCrimeOrArson", "aggressiveTowardOthers", "knowsAnyoneAtFacility", "previousProgram", "willingToParticipate",
] as const;

const hasValue = (value: unknown) => String(value ?? "").trim().length > 0;
const rowsComplete = (rows: Array<Record<string, string>>, fields: string[]) => rows.length > 0 && rows.every((row) => fields.every((field) => hasValue(row[field])));

const emptyForm = () => ({
  guestFirstName: "",
  guestLastName: "",
  guestEmail: "",
  guestPhone: "",
  guestFax: "",
  referralSource: "",
  referringOrganization: "",
  referralDate: losAngelesToday() as Date | null,
  residentFirstName: "",
  residentLastName: "",
  residentDob: null as Date | null,
  gender: "",
  residentPhone: "",
  providerOneNumber: "",
  ssnLastFour: "",
  veteranStatus: "",
  currentStatus: "",
  currentStatusOther: "",
  hospitalName: "",
  primaryCarePhysician: "",
  attendingDoctorNpiNumber: "",
  attendingDoctorName: "",
  nurseProgressNotesLastThreeDays: "",
  nextOfKinName: "",
  nextOfKinRelationship: "",
  nextOfKinPhone: "",
  nextOfKinEmail: "",
  insuranceCoverage: "",
  insuranceProvider: "",
  insurancePolicyNumber: "",
  insuranceGroupNumber: "",
  insuranceEffectiveDate: "",
  deliveryMethod: "upload" as "upload",
  faxConfirmed: false,
  clinicalScreening: {
    needsDetox: "", fCodes: "", detoxCompletedDate: "", drugOfChoice: "", methodOfUse: "", lastUseDate: "", benzoUseLast30Days: "",
    currentSuicidalIdeation: "", suicidalIdeationLast90Days: "", suicideAttemptHistory: "", selfHarm: "", psychoticSymptoms: "", groupOrRoommateSuitability: "", mentalHealthServicesBenefit: "", cognitiveImpairments: "", mentalHealthDiagnosis: [] as string[], mentalHealthHospitalizationDate: "",
    canUseStairs: "", medicalCareOrLabsNext30Days: "", hasMedicalDiagnoses: "", medicalDiagnoses: [{ condition: "", treatmentStatus: "", details: "" }], dietaryRestrictions: "", openWounds: "", homeCareReferralRequired: "", pregnant: "", pregnancyDetails: "", medicalHospitalizationLastSixMonths: "", medicalHospitalizationDetails: "", medicalProblems: "", medicalProblemsAttention: "", lifeThreateningIllness: "", lifeThreateningDetails: "", seizureHistory: "", onDialysis: "", dialysisCenterName: "", dialysisCenterAddress: "", dialysisCenterPhone: "", dialysisCenterFax: "", dialysisTransportationScheduled: "", dialysisTransportationNotes: "",
    methadoneOrSuboxone: "", medicationAssistedTreatment: "", benzoStimulantOrOpioid: "", hasMedicationsNext30Days: "", prescribedMedications: "",
    developmentalDelays: "", sexCrimeOrArson: "", aggressiveTowardOthers: "", knowsAnyoneAtFacility: "", previousProgram: "", willingToParticipate: "",
  } as ClinicalScreeningForm,
});

const inputStyle = { width: "100%" };
const sectionStyle: React.CSSProperties = { display: "grid", gap: "1rem" };

const PublicIntakePage = () => {
  const { token = "" } = useParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const captchaRef = useRef<ReCaptcha>(null);
  const [config, setConfig] = useState<PublicIntakeConfig | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [files, setFiles] = useState<File[]>([]);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [faxDeliveryStatus, setFaxDeliveryStatus] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [missingFieldIds, setMissingFieldIds] = useState<string[]>([]);

  useEffect(() => {
    setLoading(true);
    backendAPIStore.getPublicIntakeConfig(token)
      .then(setConfig)
      .catch(() => setError("This request form is unavailable. Please contact the facility directly."))
      .finally(() => setLoading(false));
  }, [backendAPIStore, token]);

  const brandName = useMemo(() => window.location.hostname.toLowerCase().includes("onehubforcare") ? "One Hub For Care" : "AFH Hub", []);
  const isAdminUser = Boolean(authStore?.isAdmin?.());
  const clearMissingFields = (...ids: string[]) => setMissingFieldIds((current) => current.filter((id) => !ids.includes(id)));
  const update = (field: string, value: any) => {
    clearMissingFields(field);
    setForm((current) => ({ ...current, [field]: value }));
  };
  const updateClinicalScreening = (field: string, value: any) => {
    clearMissingFields(`screening-${field}`, `screening-row-${field}`);
    setForm((current) => ({
      ...current,
      clinicalScreening: { ...current.clinicalScreening, [field]: value },
    }));
  };
  const updateClinicalRow = (field: string, index: number, key: string, value: string) => {
    clearMissingFields(`${field}-${index}-${key}`);
    setForm((current) => ({
      ...current,
      clinicalScreening: {
        ...current.clinicalScreening,
        [field]: (current.clinicalScreening[field] as Array<Record<string, string>>).map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
      },
    }));
  };

  useEffect(() => {
    document.querySelectorAll(".intake-missing-field").forEach((element) => element.classList.remove("intake-missing-field"));
    missingFieldIds.forEach((id) => document.getElementById(id)?.classList.add("intake-missing-field"));
  }, [missingFieldIds]);
  const addClinicalRow = (field: string, emptyRow: Record<string, string>) => setForm((current) => ({
    ...current,
    clinicalScreening: { ...current.clinicalScreening, [field]: [...(current.clinicalScreening[field] as Array<Record<string, string>>), emptyRow] },
  }));
  const removeClinicalRow = (field: string, index: number) => setForm((current) => ({
    ...current,
    clinicalScreening: { ...current.clinicalScreening, [field]: (current.clinicalScreening[field] as Array<Record<string, string>>).filter((_, rowIndex) => rowIndex !== index) },
  }));

  const fillTestData = () => {
    const yesNo = () => Math.random() >= 0.5 ? "Yes" : "No";
    const clinicalScreening = { ...emptyForm().clinicalScreening };
    SAMPLE_YES_NO_FIELDS.forEach((field) => { clinicalScreening[field] = yesNo(); });
    clinicalScreening.homeCareReferralRequired = clinicalScreening.openWounds === "Yes" ? yesNo() : "";
    clinicalScreening.medicalProblemsAttention = clinicalScreening.medicalProblems === "Yes" ? "Sample medical attention needed" : "";
    ["fCodes", "drugOfChoice", "methodOfUse", "dietaryRestrictions", "pregnancyDetails", "medicalHospitalizationDetails", "lifeThreateningDetails", "medicationAssistedTreatment", "prescribedMedications"].forEach((field) => { clinicalScreening[field] = "N/A"; });
    clinicalScreening.mentalHealthDiagnosis = ["N/A"];
    clinicalScreening.detoxCompletedDate = "2026-01-10";
    clinicalScreening.lastUseDate = "2026-01-12";
    clinicalScreening.medicalDiagnoses = clinicalScreening.hasMedicalDiagnoses === "Yes"
      ? [{ condition: "Sample condition", treatmentStatus: "Stable", details: "Sample details" }]
      : [{ condition: "", treatmentStatus: "", details: "" }];
    clinicalScreening.dialysisCenterName = clinicalScreening.onDialysis === "Yes" ? "Sample Dialysis Center" : "";
    clinicalScreening.dialysisCenterAddress = clinicalScreening.onDialysis === "Yes" ? "123 Sample Street, Seattle, WA" : "";
    clinicalScreening.dialysisCenterPhone = clinicalScreening.onDialysis === "Yes" ? "206-555-0120" : "";
    clinicalScreening.dialysisCenterFax = clinicalScreening.onDialysis === "Yes" ? "206-555-0121" : "";
    clinicalScreening.dialysisTransportationScheduled = clinicalScreening.onDialysis === "Yes" ? yesNo() : "";
    clinicalScreening.dialysisTransportationNotes = clinicalScreening.dialysisTransportationScheduled === "Yes" ? "Sample transportation schedule" : "";
    setForm({
      ...emptyForm(),
      guestFirstName: "Taylor", guestLastName: "Jordan", guestEmail: "taylor.jordan@example.com", guestPhone: "206-555-0100", guestFax: "206-555-0101",
      referralSource: "hospital", referringOrganization: "Sample Hospital", referralDate: losAngelesToday(),
      residentFirstName: "Alex", residentLastName: "Morgan", residentDob: new Date(1978, 5, 15, 12, 0, 0), gender: "Unknown", residentPhone: "206-555-0102", providerOneNumber: "123456789", ssnLastFour: "1234", veteranStatus: yesNo().toLowerCase(), currentStatus: "shelter",
      hospitalName: "Sample Hospital", primaryCarePhysician: "Dr. Smith", attendingDoctorNpiNumber: "1234567890", attendingDoctorName: "Dr. Smith", nurseProgressNotesLastThreeDays: "N/A",
      nextOfKinName: "Casey Morgan", nextOfKinRelationship: "Sibling", nextOfKinPhone: "206-555-0103", nextOfKinEmail: "casey.morgan@example.com",
      insuranceCoverage: "Apple Health", insuranceProvider: "Sample Health Plan", insurancePolicyNumber: "POL-123456", insuranceGroupNumber: "GRP-123456", insuranceEffectiveDate: "2026-01-01",
      clinicalScreening,
    });
    setError("");
  };

  const intakeFileItemTemplate = (fileObject: object, props: any) => {
    const file = fileObject as File;
    return (
      <div className="intake-file-row">
      <div className="intake-file-icon" aria-hidden="true">
        <i className={file.type.startsWith("image/") ? "pi pi-image" : file.type === "application/pdf" ? "pi pi-file-pdf" : "pi pi-file-word"} />
      </div>
      <div className="intake-file-details">
        <div className="intake-file-name" title={file.name}>{file.name}</div>
        <div className="flex align-items-center gap-2 flex-wrap mt-1">
          <span className="text-sm text-color-secondary">{props.formatSize}</span>
          <Tag value="Ready to submit" severity="warning" rounded />
        </div>
      </div>
      <Button
        type="button"
        icon="pi pi-times"
        label="Remove"
        severity="warning"
        outlined
        rounded
        aria-label={`Remove ${file.name}`}
        onClick={props.onRemove}
      />
      </div>
    );
  };

  const collectMissingFieldIds = () => {
    const missing = new Set<string>();
    const addIfMissing = (id: string, value: unknown) => { if (!hasValue(value)) missing.add(id); };
    ["guestFirstName", "guestLastName", "guestEmail", "guestPhone", "guestFax", "referralSource", "referringOrganization", "residentFirstName", "residentLastName", "residentPhone", "providerOneNumber", "ssnLastFour", "veteranStatus", "currentStatus", "primaryCarePhysician", "attendingDoctorNpiNumber", "attendingDoctorName", "nextOfKinName", "nextOfKinRelationship", "nextOfKinPhone", "nextOfKinEmail", "insuranceCoverage", "insuranceProvider", "insurancePolicyNumber", "insuranceGroupNumber", "insuranceEffectiveDate", "nurseProgressNotesLastThreeDays"].forEach((field) => addIfMissing(field, form[field as keyof typeof form]));
    if (!form.referralDate) missing.add("referralDate");
    if (!form.residentDob) missing.add("residentDob");
    if (!hasValue(form.gender)) missing.add("gender");
    if (form.currentStatus === "other") addIfMissing("currentStatusOther", form.currentStatusOther);
    REQUIRED_CLINICAL_FIELDS.forEach((field) => {
      if (!hasValue(form.clinicalScreening[field])) missing.add(SAMPLE_YES_NO_FIELDS.includes(field as typeof SAMPLE_YES_NO_FIELDS[number]) ? `screening-row-${field}` : `screening-${field}`);
    });
    if (form.clinicalScreening.hasMedicalDiagnoses === "Yes") form.clinicalScreening.medicalDiagnoses.forEach((row: Record<string, string>, index: number) => ["condition", "treatmentStatus", "details"].forEach((key) => addIfMissing(`medicalDiagnoses-${index}-${key}`, row[key])));
    if (form.clinicalScreening.onDialysis === "Yes") {
      ["dialysisCenterName", "dialysisCenterAddress", "dialysisCenterPhone", "dialysisCenterFax", "dialysisTransportationScheduled"].forEach((field) => addIfMissing(field === "dialysisTransportationScheduled" ? `screening-row-${field}` : `screening-${field}`, form.clinicalScreening[field]));
      addIfMissing("screening-dialysisTransportationNotes", form.clinicalScreening.dialysisTransportationNotes);
    }
    if (form.clinicalScreening.openWounds === "Yes") addIfMissing("screening-row-homeCareReferralRequired", form.clinicalScreening.homeCareReferralRequired);
    if (form.clinicalScreening.medicalProblems === "Yes") addIfMissing("screening-medicalProblemsAttention", form.clinicalScreening.medicalProblemsAttention);
    if (form.clinicalScreening.lifeThreateningIllness === "Yes") addIfMissing("screening-lifeThreateningDetails", form.clinicalScreening.lifeThreateningDetails);
    if (!files.length) missing.add("intakeDocuments");
    if (!captchaToken) missing.add("intakeCaptcha");
    return Array.from(missing);
  };

  const validate = () => {
    const requiredFormFields = [
      form.guestFirstName, form.guestLastName, form.guestEmail, form.guestPhone, form.guestFax, form.referralSource, form.referringOrganization,
      form.residentFirstName, form.residentLastName, form.residentPhone, form.providerOneNumber, form.ssnLastFour, form.veteranStatus, form.currentStatus,
      form.primaryCarePhysician, form.attendingDoctorNpiNumber, form.attendingDoctorName, form.nextOfKinName, form.nextOfKinRelationship,
      form.nextOfKinPhone, form.nextOfKinEmail, form.insuranceCoverage, form.insuranceProvider, form.insurancePolicyNumber, form.insuranceGroupNumber, form.insuranceEffectiveDate,
      form.nurseProgressNotesLastThreeDays,
    ];
    if (requiredFormFields.some((value) => !hasValue(value)) || !form.referralDate) return "Complete every contact, referral, resident, primary care, and insurance field.";
    if (!/^\S+@\S+\.\S+$/.test(form.guestEmail.trim())) return "Enter a valid email address.";
    if (!/^\S+@\S+\.\S+$/.test(form.nextOfKinEmail.trim())) return "Enter a valid next of kin email address.";
    if (!form.residentDob || !form.gender) return "Complete every resident field.";
    if (!/^\d{4}$/.test(form.ssnLastFour)) return "SSN last four must contain exactly four digits.";
    if (form.currentStatus === "other" && !hasValue(form.currentStatusOther)) return "Enter the other current status.";
    if (REQUIRED_CLINICAL_FIELDS.some((field) => !hasValue(form.clinicalScreening[field]))) return "Complete every clinical screening field.";
    if (form.clinicalScreening.hasMedicalDiagnoses === "Yes" && !rowsComplete(form.clinicalScreening.medicalDiagnoses, ["condition", "treatmentStatus", "details"])) return "Complete every medical diagnosis detail.";
    if (form.clinicalScreening.onDialysis === "Yes") {
      if (["dialysisCenterName", "dialysisCenterAddress", "dialysisCenterPhone", "dialysisCenterFax", "dialysisTransportationScheduled"].some((field) => !hasValue(form.clinicalScreening[field]))) return "Complete every dialysis detail.";
      if (!hasValue(form.clinicalScreening.dialysisTransportationNotes)) return "Enter the dialysis transportation notes.";
    }
    if (form.clinicalScreening.openWounds === "Yes" && !hasValue(form.clinicalScreening.homeCareReferralRequired)) return "Select whether a home care referral is required for wound care.";
    if (form.clinicalScreening.medicalProblems === "Yes" && !hasValue(form.clinicalScreening.medicalProblemsAttention)) return "List the medical problems that need attention.";
    if (form.clinicalScreening.lifeThreateningIllness === "Yes" && !hasValue(form.clinicalScreening.lifeThreateningDetails)) return "Enter the life-threatening illness details.";
    if (files.length < 1) return "Select at least one document to upload.";
    if (!captchaToken) return "Complete the CAPTCHA verification.";
    return "";
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      const missingIds = collectMissingFieldIds();
      setMissingFieldIds(missingIds);
      setError(validationError);
      requestAnimationFrame(() => document.getElementById(missingIds[0])?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      let intakeSubmissionId = "";
      if (form.deliveryMethod === "upload") {
        const prepared = await backendAPIStore.preparePublicIntakeUploads({
          token,
          recaptchaToken: captchaToken,
          files: files.map((file) => ({ fileName: file.name, contentType: file.type, size: file.size })),
        });
        intakeSubmissionId = prepared.intakeSubmissionId;
        await Promise.all(prepared.uploads.map((upload: any, index: number) => backendAPIStore.uploadPublicIntakeDocument(upload.uploadUrl, files[index])));
      }
      const result = await backendAPIStore.submitPublicIntake({
        token,
        intakeSubmissionId: intakeSubmissionId || undefined,
        recaptchaToken: undefined,
        brandKey: brandName,
        guestContact: {
          firstName: form.guestFirstName,
          lastName: form.guestLastName,
          emailAddress: form.guestEmail,
          phoneNumber: form.guestPhone,
          faxNumber: form.guestFax,
        },
        referralInformation: {
          referralSource: form.referralSource,
          referringOrganization: form.referringOrganization,
          referralDate: formatDateOnly(form.referralDate),
        },
        residentDetails: {
          firstName: form.residentFirstName,
          lastName: form.residentLastName,
          dateOfBirth: formatDateOnly(form.residentDob),
          gender: form.gender,
          phoneNumber: form.residentPhone,
          providerOneNumber: form.providerOneNumber,
          ssnLastFour: form.ssnLastFour,
          veteranStatus: form.veteranStatus,
          currentStatus: form.currentStatus,
          currentStatusOther: form.currentStatusOther,
          hospitalName: form.hospitalName,
          primaryCarePhysician: form.primaryCarePhysician,
          attendingDoctorNpiNumber: form.attendingDoctorNpiNumber,
          attendingDoctorName: form.attendingDoctorName,
          nurseProgressNotesLastThreeDays: form.nurseProgressNotesLastThreeDays,
          nextOfKinName: form.nextOfKinName,
          nextOfKinRelationship: form.nextOfKinRelationship,
          nextOfKinPhone: form.nextOfKinPhone,
          nextOfKinEmail: form.nextOfKinEmail,
          insuranceCoverage: form.insuranceCoverage,
          insuranceProvider: form.insuranceProvider,
          insurancePolicyNumber: form.insurancePolicyNumber,
          insuranceGroupNumber: form.insuranceGroupNumber,
          insuranceEffectiveDate: form.insuranceEffectiveDate,
        },
        documentDelivery: {
          method: form.deliveryMethod,
          faxConfirmed: form.faxConfirmed,
        },
        clinicalScreening: form.clinicalScreening,
      });
      setReferenceNumber(result.referenceNumber);
      setFaxDeliveryStatus(String(result.faxDeliveryStatus || ""));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || requestError?.message || "Unable to submit the request form. Please try again.");
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}><ProgressSpinner /></div>;
  if (!config) return <div className="p-4 mx-auto" style={{ maxWidth: 760 }}><Message severity="error" text={error} /></div>;
  if (referenceNumber) return (
    <main className="p-3 md:p-5 mx-auto" style={{ maxWidth: 760 }}>
      <Card>
        <div className="text-center flex flex-column align-items-center gap-3 py-5">
          <i className="pi pi-check-circle text-green-500" style={{ fontSize: "4rem" }} />
          <h1 className="m-0">Request received</h1>
          <p className="text-color-secondary line-height-3">{config.home.businessName} received your request. An acknowledgment was sent to {form.guestEmail}.</p>
          <Message severity="success" text={`Reference number: ${referenceNumber}`} />
          {faxDeliveryStatus === "QUEUED" || faxDeliveryStatus === "SUBMITTED" ? <Message severity="info" text="Your uploaded documents are being securely faxed to the facility." /> : null}
          {faxDeliveryStatus === "SKIPPED_NO_HOME_FAX" ? <Message severity="warn" text="Your request was received. The facility will review the uploaded documents directly." /> : null}
          <p className="text-sm text-color-secondary">Keep this reference number for your records.</p>
          <Button type="button" label="Submit New Request" icon="pi pi-refresh" severity="warning" rounded onClick={() => window.location.reload()} />
        </div>
      </Card>
    </main>
  );

  const textField = (id: string, label: string, value: string, required = true, type = "text") => (
    <span className="p-float-label">
      <InputText id={id} type={type} value={value} required={required} onChange={(event) => update(id, event.target.value)} style={inputStyle} />
      <label htmlFor={id}>{label}</label>
    </span>
  );

  const screeningTextField = (field: string, label: string, textarea = false) => textarea ? (
    <span className="p-float-label">
      <InputTextarea id={`screening-${field}`} value={form.clinicalScreening[field as keyof typeof form.clinicalScreening]} required onChange={(event) => updateClinicalScreening(field, event.target.value)} rows={3} className="w-full" />
      <label htmlFor={`screening-${field}`}>{label}</label>
    </span>
  ) : (
    <span className="p-float-label">
      <InputText id={`screening-${field}`} value={form.clinicalScreening[field as keyof typeof form.clinicalScreening]} required onChange={(event) => updateClinicalScreening(field, event.target.value)} className="w-full" />
      <label htmlFor={`screening-${field}`}>{label}</label>
    </span>
  );

  const screeningYesNo = (field: string, label: string) => (
    <div id={`screening-row-${field}`} className="intake-yes-no-row py-1">
      <span className="font-medium">{label}</span>
      <div className="flex align-items-center gap-4 flex-shrink-0">
        {["Yes", "No"].map((value) => <div className="flex align-items-center gap-2" key={value}><RadioButton inputId={`screening-${field}-${value.toLowerCase()}`} name={`screening-${field}`} value={value} checked={form.clinicalScreening[field as keyof typeof form.clinicalScreening] === value} onChange={(event) => updateClinicalScreening(field, String(event.value))} /><label htmlFor={`screening-${field}-${value.toLowerCase()}`}>{value}</label></div>)}
      </div>
    </div>
  );

  const repeatableClinicalRows = (field: string, title: string, columns: Array<{ key: string; label: string; className: string }>, emptyRow: Record<string, string>, allowDelete = false) => {
    const rows = form.clinicalScreening[field] as Array<Record<string, string>>;
    return (
      <div className="col-12 flex flex-column gap-2">
        <div className="flex justify-content-between align-items-center gap-2 flex-wrap"><div className="font-semibold">{title}</div><Button type="button" label="Add another" icon="pi pi-plus" severity="warning" outlined rounded size="small" onClick={() => addClinicalRow(field, emptyRow)} /></div>
        {rows.map((row, index) => <div key={`${field}-${index}`} className="grid border-1 border-round p-2" style={{ borderColor: "#fdba74", background: "#fffaf5" }}>
          {columns.map((column) => <div className={column.className} key={column.key}><span className="p-float-label"><InputText id={`${field}-${index}-${column.key}`} value={row[column.key]} required onChange={(event) => updateClinicalRow(field, index, column.key, event.target.value)} className="w-full" /><label htmlFor={`${field}-${index}-${column.key}`}>{column.label}</label></span></div>)}
          {allowDelete ? <div className="col-12 lg:col-4 flex align-items-center justify-content-end"><Button type="button" icon="pi pi-trash" severity="danger" text rounded aria-label={`Remove ${title.toLowerCase()} entry ${index + 1}`} tooltip="Remove entry" tooltipOptions={{ position: "top" }} onClick={() => removeClinicalRow(field, index)} /></div> : null}
        </div>)}
      </div>
    );
  };

  const wizardSteps = ["Contact & referral", "Resident", "Clinical screening", "Documents"];
  const stepperItems = wizardSteps.map((label, index) => ({ label, command: () => setCurrentStep(index) }));
  const formSectionTitles = ["Contact & referral", "Resident details", "Clinical screening", "Documents"];

  return (
    <main className="intake-public-page p-2 md:p-5" style={{ background: "#f6f9fc", minHeight: "100vh" }}>
      <div className="w-full flex flex-column gap-3">
        {isAdminUser ? <div className="intake-summary-card flex justify-content-end">
          <Button type="button" label="Fill test data" icon="pi pi-file-edit" severity="secondary" outlined size="small" onClick={fillTestData} />
        </div> : null}
        <div className="grid">
          <div className="col-12">
        <Card className="intake-header-card intake-summary-card">
          <div className="intake-facility-details text-center">
                  <h1 className="mt-0 mb-1 text-2xl">{config.home.businessName}</h1>
                  <h2 className="text-lg font-medium mt-0 mb-2">{config.title}</h2>
            <div className="flex justify-content-center gap-3 flex-wrap text-sm">
              {config.home.businessPhoneNumber ? <span><i className="pi pi-phone mr-2" />{config.home.businessPhoneNumber}</span> : null}
              {config.home.businessFaxNumber ? <span><i className="pi pi-print mr-2" />Fax: {config.home.businessFaxNumber}</span> : null}
              {config.home.providerEmail ? <span><i className="pi pi-envelope mr-2" />{config.home.providerEmail}</span> : null}
            </div>
          </div>
          <Divider className="my-3" />
          <div className="intake-help-guide flex flex-column align-items-center text-center gap-2">
            <i className="pi pi-info-circle text-primary" />
            <div>
              <p className="text-color-secondary line-height-3 m-0 text-sm">Send referral and prospective resident information, along with any supporting documents, to {config.home.businessName} for review.</p>
              {config.instructions ? <p className="text-color-secondary line-height-3 white-space-pre-line mb-0 mt-2 text-sm">{config.instructions}</p> : null}
              <div className="flex flex-wrap justify-content-center gap-2 mt-3">
                <Tag value="Secure eFax" icon="pi pi-print" severity="warning" rounded />
                <Tag value="HIPAA safeguards" icon="pi pi-shield" severity="success" rounded />
              </div>
            </div>
          </div>
          <div className="intake-help-guide hidden">
            <i className="pi pi-info-circle text-primary mt-1" />
            <div>
              <div className="font-semibold mb-1">Help guide</div>
              <p className="text-color-secondary line-height-3 m-0 text-sm">
                Use this form to send referral and prospective resident information to {config.home.businessName} for review. You can upload supporting documents or confirm that you sent them by fax. The facility will review your submission and contact you if additional information is required. Submission does not guarantee admission.
              </p>
            </div>
          </div>
        </Card>
          </div>
          <div className="hidden">
        <div className="hidden"><Card className="intake-header-card">
          <div className="hidden">
            {wizardSteps.map((label, index) => {
              const active = currentStep === index;
              const complete = currentStep > index;
              return (
                <React.Fragment key={label}>
                  <Button type="button" onClick={() => setCurrentStep(index)} style={{ border: active ? "1px solid #f97316" : "1px solid #dbe7f3", background: active ? "#fff7ed" : "#ffffff", color: active ? "#9a3412" : "#64748b", borderRadius: "999px", padding: "0.45rem 0.75rem", fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", justifyContent: "center", alignItems: "center", width: "1.35rem", height: "1.35rem", borderRadius: "50%", marginRight: "0.35rem", color: "#ffffff", background: complete || active ? "#f97316" : "#94a3b8", fontSize: "0.75rem" }}>{complete ? "✓" : index + 1}</span>{label}
                  </Button>
                  {index < wizardSteps.length - 1 ? <span className="text-color-secondary">—</span> : null}
                </React.Fragment>
              );
            })}
          </div>
            <div className="mt-2">
              <div className="font-semibold mb-2">Form sections</div>
              <Steps model={stepperItems} activeIndex={currentStep} readOnly={false} className={`intake-vertical-steps intake-stepper-progress-${currentStep}`} />
            </div>
        </Card></div>
            <Card className="intake-header-card"><div className="intake-section-list">
              <div className="font-semibold mb-2">Form sections</div>
              <ol className="m-0 p-0 list-none flex flex-column gap-2">
                {wizardSteps.map((label, index) => <li key={label} className={`flex align-items-center gap-2 ${index <= currentStep ? "intake-section-list-complete" : "text-color-secondary"}`}>
                  <span className="intake-section-list-number">{index + 1}</span>
                  <span className={index === currentStep ? "font-semibold" : undefined}>{label}</span>
                </li>)}
              </ol>
            </div></Card>
          </div>
        </div>

        <Card className="intake-form-card">
          <div style={sectionStyle}>
            <div className="font-bold text-red-600 text-right">All fields are required. Enter N/A if information is unavailable.</div>
            <h3 className="mt-0">Case manager, referral partner, or family contact information</h3>
            <div className="intake-four-column-grid">
              {textField("guestFirstName", "First name", form.guestFirstName, true)}
              {textField("guestLastName", "Last name", form.guestLastName, true)}
              {textField("guestEmail", "Email address", form.guestEmail, true, "email")}
              {textField("guestPhone", "Phone number", form.guestPhone, true)}
              {textField("guestFax", "Fax number", form.guestFax)}
              <span className="p-float-label"><Dropdown id="referralSource" value={form.referralSource} options={[{ label: "Hospital", value: "hospital" }, { label: "Organization", value: "organization" }]} onChange={(event) => update("referralSource", event.value)} style={inputStyle} /><label htmlFor="referralSource">Select referral source</label></span>
              {form.referralSource ? textField("referringOrganization", form.referralSource === "hospital" ? "Hospital name" : "Organization name", form.referringOrganization) : null}
              <span className="p-float-label"><Calendar id="referralDate" value={form.referralDate} onChange={(event) => update("referralDate", event.value || null)} dateFormat="mm/dd/yy" maxDate={losAngelesToday()} style={inputStyle} /><label htmlFor="referralDate">Date of referral</label></span>
            </div>
          </div>
        </Card>

        <Card className="intake-form-card">
          <div style={sectionStyle}>
            <h3 className="mt-0">Resident details</h3>
            <div className="intake-four-column-grid">
              {textField("residentFirstName", "First name", form.residentFirstName, true)}
              {textField("residentLastName", "Last name", form.residentLastName, true)}
              <span className="p-float-label"><Calendar id="residentDob" value={form.residentDob} onChange={(event) => update("residentDob", event.value || null)} dateFormat="mm/dd/yy" maxDate={losAngelesToday()} style={inputStyle} /><label htmlFor="residentDob">Date of birth</label></span>
              <span className="p-float-label"><Dropdown id="gender" value={form.gender} options={["Female", "Male", "Unknown", "Prefer not to say"]} onChange={(event) => update("gender", event.value)} style={inputStyle} /><label htmlFor="gender">Gender</label></span>
              {textField("residentPhone", "Phone number", form.residentPhone)}
              {textField("providerOneNumber", "Medicaid ID (ProviderOne)", form.providerOneNumber)}
              <span className="p-float-label"><InputMask id="ssnLastFour" mask="9999" value={form.ssnLastFour} required onChange={(event) => update("ssnLastFour", event.value || "")} style={inputStyle} /><label htmlFor="ssnLastFour">SSN (last 4 only)</label></span>
            </div>
            <div className="intake-four-column-grid">
              {textField("nextOfKinName", "Next of kin name", form.nextOfKinName)}
              {textField("nextOfKinRelationship", "Relationship", form.nextOfKinRelationship)}
              {textField("nextOfKinPhone", "Next of kin phone", form.nextOfKinPhone)}
              {textField("nextOfKinEmail", "Next of kin email", form.nextOfKinEmail, true, "email")}
            </div>
            <div className="intake-four-column-grid">
              <div id="veteranStatus" className="intake-column-field">
                <div className="font-semibold mb-2">Veteran status</div>
                <div className="flex gap-4">
                  {["yes", "no"].map((value) => <div className="flex align-items-center gap-2" key={value}><RadioButton inputId={`veteran-${value}`} name="veteranStatus" value={value} checked={form.veteranStatus === value} onChange={(event) => update("veteranStatus", event.value)} /><label htmlFor={`veteran-${value}`}>{value === "yes" ? "Yes" : "No"}</label></div>)}
                </div>
              </div>
              <span className="p-float-label"><Dropdown id="currentStatus" value={form.currentStatus} options={[{ label: "Homeless", value: "homeless" }, { label: "Shelter", value: "shelter" }, { label: "Transitional", value: "transitional" }, { label: "Other", value: "other" }]} onChange={(event) => update("currentStatus", event.value)} style={inputStyle} /><label htmlFor="currentStatus">Current status</label></span>
              {form.currentStatus === "other" ? textField("currentStatusOther", "Other current status", form.currentStatusOther) : null}
            </div>
            <Divider />
            <h3 className="m-0">Primary care and insurance</h3>
            <div className="intake-four-column-grid">
              {textField("primaryCarePhysician", "Primary care physician", form.primaryCarePhysician)}
              {textField("attendingDoctorNpiNumber", "NPI number of attending doctor", form.attendingDoctorNpiNumber)}
              {textField("attendingDoctorName", "Attending doctor name", form.attendingDoctorName)}
              <span className="p-float-label"><Dropdown id="insuranceCoverage" value={form.insuranceCoverage} options={["Apple Health", "Medicare", "Other"]} onChange={(event) => update("insuranceCoverage", event.value)} style={inputStyle} /><label htmlFor="insuranceCoverage">Insurance type</label></span>
              {textField("insuranceProvider", "Insurance provider", form.insuranceProvider)}
              {textField("insurancePolicyNumber", "Policy or member ID", form.insurancePolicyNumber)}
              {textField("insuranceGroupNumber", "Group number", form.insuranceGroupNumber)}
              <span className="p-float-label"><Calendar id="insuranceEffectiveDate" value={parseDateOnly(form.insuranceEffectiveDate)} onChange={(event) => update("insuranceEffectiveDate", formatDateOnly(event.value as Date | null))} dateFormat="mm/dd/yy" style={inputStyle} /><label htmlFor="insuranceEffectiveDate">Insurance effective date</label></span>
            </div>
          </div>
        </Card>

        <Card className="intake-form-card">
          <div style={sectionStyle}>
            <h3 className="mt-0">Clinical screening</h3>
            <Message severity="warn" text="Complete the sections below using the information available from the client or referral record." />
            <div className="grid">
              <div className="col-12"><h3 className="mt-0">Substance use</h3></div>
              <div className="col-12">{screeningYesNo("needsDetox", "Does the client currently need detoxification services?")}</div>
              <div className="col-12 lg:col-3">{screeningTextField("fCodes", "Medical diagnosis codes")}</div>
              <div className="col-12 lg:col-3"><span className="p-float-label"><Calendar id="screening-detoxCompletedDate" value={parseDateOnly(form.clinicalScreening.detoxCompletedDate)} onChange={(event) => updateClinicalScreening("detoxCompletedDate", formatDateOnly(event.value as Date | null))} dateFormat="mm/dd/yy" maxDate={losAngelesToday()} style={inputStyle} /><label htmlFor="screening-detoxCompletedDate">Detox completion date</label></span></div>
              <div className="col-12 lg:col-3">{screeningTextField("drugOfChoice", "Drug of choice")}</div>
              <div className="col-12 lg:col-3">{screeningTextField("methodOfUse", "Method of use")}</div>
              <div className="col-12 lg:col-3"><span className="p-float-label"><Calendar id="screening-lastUseDate" value={parseDateOnly(form.clinicalScreening.lastUseDate)} onChange={(event) => updateClinicalScreening("lastUseDate", formatDateOnly(event.value as Date | null))} dateFormat="mm/dd/yy" maxDate={losAngelesToday()} style={inputStyle} /><label htmlFor="screening-lastUseDate">Last use date</label></span></div>
              <div className="col-12">{screeningYesNo("benzoUseLast30Days", "Has the client used benzodiazepines within the past 30 days?")}</div>
              <div className="col-12"><Divider /><h3>Safety and mental health</h3></div>
              {[['currentSuicidalIdeation','Is the client currently experiencing suicidal thoughts or ideation?'],['suicidalIdeationLast90Days','Has the client experienced suicidal thoughts or ideation within the past 90 days?'],['suicideAttemptHistory','Does the client have a history of suicide attempts?'],['selfHarm','Does the client have a history of self-harm?'],['psychoticSymptoms','Is the client experiencing psychotic symptoms, delusions, or hallucinations?'],['groupOrRoommateSuitability','Can the client safely participate in large groups and share a room with another resident?'],['mentalHealthServicesBenefit','Would the client benefit from mental health services?'],['cognitiveImpairments','Does the client have known cognitive impairments?']].map(([field, label]) => <div className="col-12" key={field}>{screeningYesNo(field, label)}</div>)}
              <div className="col-12 md:col-6"><span className="p-float-label"><Chips id="screening-mentalHealthDiagnosis" value={form.clinicalScreening.mentalHealthDiagnosis as string[]} onChange={(event) => updateClinicalScreening("mentalHealthDiagnosis", event.value || [])} className="w-full" separator="," /><label htmlFor="screening-mentalHealthDiagnosis">Mental health diagnoses</label></span></div>
              <div className="col-12"><Divider /><h3>Medical and medication</h3></div>
              {[['canUseStairs','Can the client safely walk up and down stairs?'],['medicalCareOrLabsNext30Days','Does the client need medical care or laboratory tests within the next 30 days?']].map(([field, label]) => <div className="col-12" key={field}>{screeningYesNo(field, label)}</div>)}
              <div className="col-12">{screeningYesNo("hasMedicalDiagnoses", "Does the client have any medical diagnoses?")}</div>
              {form.clinicalScreening.hasMedicalDiagnoses === "Yes" ? repeatableClinicalRows("medicalDiagnoses", "Add medical diagnoses", [{ key: "condition", label: "Condition", className: "col-12 md:col-4" }, { key: "treatmentStatus", label: "Treatment status", className: "col-12 md:col-4" }, { key: "details", label: "Details or additional information", className: "col-12 md:col-4" }], { condition: "", treatmentStatus: "", details: "" }) : null}
              <div className="col-12">{screeningYesNo("onDialysis", "Is the client on dialysis?")}</div>
              {form.clinicalScreening.onDialysis === "Yes" ? <div className="col-12 intake-conditional-section">
                <div className="font-semibold">Dialysis details</div>
                <div className="grid mt-1">
                  <div className="col-12 lg:col-3">{screeningTextField("dialysisCenterName", "Dialysis center name")}</div>
                  <div className="col-12 lg:col-3">{screeningTextField("dialysisCenterAddress", "Dialysis center address")}</div>
                  <div className="col-12 lg:col-3">{screeningTextField("dialysisCenterPhone", "Dialysis center phone")}</div>
                  <div className="col-12 lg:col-3">{screeningTextField("dialysisCenterFax", "Dialysis center fax")}</div>
                  <div className="col-12">{screeningYesNo("dialysisTransportationScheduled", "Is transportation to the dialysis center scheduled?")}</div>
                  <div className="col-12">{screeningTextField("dialysisTransportationNotes", "Dialysis transportation notes", true)}</div>
                </div>
              </div> : null}
              <div className="col-12">{screeningYesNo("openWounds", "Does the client currently have any open wounds?")}</div>
              {form.clinicalScreening.openWounds === "Yes" ? <div className="col-12 intake-conditional-section"><div className="font-semibold mb-2">Home care referral for wound care</div>{screeningYesNo("homeCareReferralRequired", "Is a home care referral required?")}{form.clinicalScreening.homeCareReferralRequired === "Yes" ? <Message severity="warn" className="mt-2" text="Please schedule a home health referral for wound care." /> : null}</div> : null}
              {[['pregnant','Is the client currently pregnant?'],['medicalHospitalizationLastSixMonths','Has the client been medically hospitalized within the past six months?']].map(([field, label]) => <div className="col-12" key={field}>{screeningYesNo(field, label)}</div>)}
              <div className="col-12">{screeningYesNo("medicalProblems", "Does the client currently have medical problems that need attention?")}</div>
              {form.clinicalScreening.medicalProblems === "Yes" ? <div className="col-12">{screeningTextField("medicalProblemsAttention", "List the medical problems that need attention", true)}</div> : null}
              <div className="col-12">{screeningYesNo("lifeThreateningIllness", "Does the client have a life-threatening illness?")}</div>
              {form.clinicalScreening.lifeThreateningIllness === "Yes" ? <div className="col-12">{screeningTextField("lifeThreateningDetails", "Life-threatening illness details", true)}</div> : null}
              {[['seizureHistory','Does the client have a history of seizures?'],['methadoneOrSuboxone','Is the client currently taking Methadone or Suboxone?'],['benzoStimulantOrOpioid','Is the client currently taking benzodiazepines, stimulants, or opioids?']].map(([field, label]) => <div className="col-12" key={field}>{screeningYesNo(field, label)}</div>)}
              <div className="col-12 md:col-6">{screeningTextField("dietaryRestrictions", "Dietary restrictions or special diet", true)}</div>
              <div className="col-12 md:col-6">{screeningTextField("pregnancyDetails", "Pregnancy details")}</div>
              <div className="col-12 md:col-6">{screeningTextField("medicalHospitalizationDetails", "Hospitalization details", true)}</div>
              <div className="col-12 md:col-6">{screeningTextField("medicationAssistedTreatment", "Which medication-assisted treatment?")}</div>
              <div className="col-12">{screeningYesNo("hasMedicationsNext30Days", "Does the client have enough medication for the next 30 days?")}</div>
              {form.clinicalScreening.hasMedicationsNext30Days === "No" ? <div className="col-12 intake-conditional-section"><div className="flex flex-column gap-2"><div className="font-semibold">Please send the medication to the home pharmacy:</div><div className="flex flex-column gap-1 text-sm"><div><strong>Pharmacy name:</strong> {config.home.pharmacyName || "Not provided"}</div><div><strong>Address:</strong> <strong>{config.home.pharmacyAddress || "Not provided"}</strong></div><div><strong>Phone number:</strong> {config.home.pharmacyPhoneNumber || "Not provided"}</div></div></div></div> : null}
              <div className="col-12"><Divider /><h3>Legal, social, and program goals</h3></div>
              {[['developmentalDelays','Does the client have developmental delays?'],['sexCrimeOrArson','Has the client been convicted of a sex crime or arson?'],['aggressiveTowardOthers','Has the client been aggressive toward other people?'],['knowsAnyoneAtFacility','Does the client know anyone currently living at the facility?'],['previousProgram','Has the client previously participated in this program?']].map(([field, label]) => <div className="col-12" key={field}>{screeningYesNo(field, label)}</div>)}
              <div className="col-12">{screeningYesNo("willingToParticipate", "Is the client willing to participate in program expectations?")}</div>
              <div className="col-12"><span className="p-float-label"><InputTextarea id="nurseProgressNotesLastThreeDays" value={form.nurseProgressNotesLastThreeDays} onChange={(event) => update("nurseProgressNotesLastThreeDays", event.target.value)} rows={5} className="w-full" /><label htmlFor="nurseProgressNotesLastThreeDays">Nurse progress notes from the last 3 days (optional)</label></span></div>
            </div>
          </div>
        </Card>

        <Card className="intake-form-card">
          <div style={sectionStyle}>
            <h3 className="mt-0">Documents</h3>
            <p className="text-color-secondary mt-0">Upload any supporting documents you would like the facility to review.</p>
            <Message severity="info" className="text-center" content={<div className="w-full text-center"><strong>Secure eFax delivery</strong><div className="mt-1">After submission, Hub will securely eFax all uploaded documents to the facility.</div></div>} />
            <div id="intakeDocuments" className="intake-document-column intake-document-column-full">
                <div className="intake-file-list-heading">
                  <div>
                    <div className="font-semibold">Files to submit</div>
                    <small className="text-color-secondary">Review the selected documents before submitting the request.</small>
                  </div>
                  <Tag value={`${files.length} of ${config.maxDocuments}`} severity="warning" rounded />
                </div>
                <FileUpload
                  className="intake-advanced-upload"
                  mode="advanced"
                  name="intakeDocuments"
                  multiple
                  customUpload
                  uploadHandler={() => undefined}
                  chooseOptions={{
                    label: files.length ? "Add documents" : "Choose documents",
                    icon: "pi pi-plus",
                    className: "p-button-warning p-button-outlined p-button-rounded",
                  }}
                  uploadOptions={{ style: { display: "none" } }}
                  cancelOptions={{
                    label: "Clear list",
                    icon: "pi pi-times",
                    className: "p-button-warning p-button-outlined p-button-rounded",
                  }}
                  accept={config.acceptedContentTypes.join(",")}
                  maxFileSize={config.maxDocumentBytes}
                  itemTemplate={intakeFileItemTemplate}
                  emptyTemplate={(
                    <div className="intake-upload-empty">
                      <i className="pi pi-cloud-upload" aria-hidden="true" />
                      <div className="font-semibold">Drag documents here</div>
                      <small>PDF, Word, or text files. Maximum 10 MB each.</small>
                    </div>
                  )}
                  onSelect={(event) => {
                    const selected = Array.from(event.files || []).slice(0, config.maxDocuments) as File[];
                    setFiles(selected);
                    if (selected.length) clearMissingFields("intakeDocuments");
                    if ((event.files || []).length > config.maxDocuments) setError(`You can upload up to ${config.maxDocuments} documents.`);
                  }}
                  onRemove={(event) => setFiles((current) => current.filter((file) => file !== event.file))}
                  onClear={() => setFiles([])}
                />
            </div>
          </div>
        </Card>

        <Card className="intake-form-card">
          <div style={sectionStyle}>
            <h3 className="mt-0">Review and submit</h3>
            <div className="surface-100 border-round p-3 text-center">
              <div className="flex flex-wrap justify-content-center align-items-center gap-2 mb-2">
                <Tag value="Secure eFax" icon="pi pi-print" severity="warning" rounded />
                <Tag value="HIPAA safeguards" icon="pi pi-shield" severity="success" rounded />
              </div>
              <div className="font-semibold">Secure document delivery</div>
              <p className="text-sm text-color-secondary line-height-3 my-2">Hub uses secure eFax to send submitted documents to the facility eFax number.</p>
              <div className="text-sm">Facility eFax: <strong>{config.home.businessFaxNumber || "Not provided"}</strong></div>
            </div>
            <Divider />
            <div id="intakeCaptcha" className="flex justify-content-center"><ReCaptcha ref={captchaRef} sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY || ""} onChange={(value) => { clearMissingFields("intakeCaptcha"); setCaptchaToken(value); }} onExpired={() => setCaptchaToken(null)} /></div>
            {error ? <Message severity="error" text={error} /> : null}
            <div className="flex justify-content-center">
              <Button className="intake-submit-button" label="Submit Request" icon="pi pi-send" severity="warning" rounded size="large" loading={submitting} disabled={submitting || !captchaToken} onClick={() => void submit()} />
            </div>
            <small className="text-center text-color-secondary">After submitting this form, the facility will review the information and contact you if required.</small>
          </div>
        </Card>
        <div className="hidden">
          <Button type="button" label="Back" icon="pi pi-arrow-left" severity="secondary" outlined rounded disabled={currentStep === 0} onClick={() => setCurrentStep((step) => Math.max(0, step - 1))} />
          {currentStep < wizardSteps.length - 1 ? <Button type="button" label={`Next: ${wizardSteps[currentStep + 1]}`} icon="pi pi-arrow-right" iconPos="right" severity="warning" rounded onClick={() => { setError(""); setCurrentStep((step) => Math.min(wizardSteps.length - 1, step + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }} /> : null}
        </div>
      </div>
    </main>
  );
};

export default PublicIntakePage;
